'use client'
import { ChevronRightIcon } from '@repo/ui/ChevronRightIcon'
import { MicrosoftLogo } from '@repo/ui/MicrosoftLogo'
import { useAuthenticationStore } from '@repo/store/useAuthenticationStore'
import { useEmailStore } from '@repo/store/useEmailStore'
import { useGlobalSettingsStore } from '@repo/store/useGlobalSettingsStore'
import { useEffect, useState } from 'react'
import MUIButton from '@mui/material/Button'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'

const EmailSetup = () => {
  const { fetchEmailAuthUrl, deleteEmailSubscriptions, deleteEmailTokens } = useEmailStore()
  const { hasGraphApiTokens, hasGraphApiSubscriptions, getProfile } =
    useAuthenticationStore()
  const { useMicrosoftEmail, companyCanUseMicrosoftEmail } = useGlobalSettingsStore()
  const [needsEmailSetup, setNeedsEmailSetup] = useState(!hasGraphApiTokens || !hasGraphApiSubscriptions)

  useEffect(() => {
    getProfile()
  }, [getProfile])

  useEffect(() => {
    setNeedsEmailSetup(!hasGraphApiTokens || !hasGraphApiSubscriptions)
  }, [hasGraphApiTokens, hasGraphApiSubscriptions])

  const startEmailIntegration = async () => {
    const authUrl = await fetchEmailAuthUrl()
    location.replace(authUrl)
  }

  const resetEmailIntegration = async () => {
    await deleteEmailSubscriptions()
    await deleteEmailTokens()

    // this will update the values of hasGraphApiTokens and hasGraphApiSubscriptions in the store
    // which in turn will trigger the useEffect that updates the needsEmailSetup state
    await getProfile()
  }

  if (useMicrosoftEmail === false || companyCanUseMicrosoftEmail === false) {
    return (
      <MUIStack
        data-testid="EmailSetup"
        gap={6}
        sx={{ maxWidth: 680, margin: 'auto', backgroundColor: 'white', borderRadius: '6px', padding: '60px 40px' }}
      >
        <MicrosoftLogo sx={{ width: '220px', height: 'auto', marginBottom: 3 }} />
        <MUITypography variant="body1">
          {`This site is not configured to support Osprey's Microsoft email integration. Please contact support for more information.`}
        </MUITypography>
      </MUIStack>
    )
  }

  return (
    <MUIStack
      data-testid="EmailSetup"
      gap={6}
      sx={{ maxWidth: 680, margin: 'auto', backgroundColor: 'white', borderRadius: '6px', padding: '60px 40px' }}
    >
      <MicrosoftLogo sx={{ width: '220px', height: 'auto', marginBottom: 3 }} />

      {needsEmailSetup ? (
        <>
          <MUITypography variant="body1">
            {`This screen enables you to set up a Microsoft email integration, which will allow you to send emails via your
        Microsoft account within Osprey, and to enable Matter History auto-filing.`}
          </MUITypography>

          <MUITypography variant="body1">
            {`When you click the button below, you'll be redirected to a Microsoft login page. You'll need to grant permission to the Microsoft Graph API. Once you've done that, you'll be redirected back to Osprey.`}
          </MUITypography>
          <MUIButton
            variant="contained"
            color="primary"
            size="large"
            onClick={startEmailIntegration}
            sx={{ maxWidth: 160, width: '100%', margin: '20px 0 0 auto' }}
          >
            Start
            <ChevronRightIcon sx={{ color: 'white' }} />
          </MUIButton>
        </>
      ) : (
        <>
          <MUITypography variant="body1">
            {`You have previously set up a Microsoft email integration, allowing you to send emails via your Microsoft account within Osprey.`}
          </MUITypography>

          <MUITypography variant="body1">
            {`If you wish to remove the integration, you can do so by clicking the button below.`}
          </MUITypography>
          <MUIButton
            variant="contained"
            color="error"
            size="large"
            onClick={resetEmailIntegration}
            sx={{ maxWidth: 160, width: '100%', margin: '20px 0 0 auto' }}
          >
            Remove
          </MUIButton>
        </>
      )}
    </MUIStack>
  )
}

export default EmailSetup
